import React, { useState, useRef, useEffect } from 'react';
import useSupercluster from "use-supercluster";
import GoogleMapReact from "google-map-react";
import { Marker } from 'react-google-maps';
import InfoWindow from '../InfoWindow';
import './Map.css';

// Estilos melhorados para o tema escuro do mapa com maior contraste e nitidez
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1f2a37" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }, { saturation: 50 }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4a5568" }]
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#e9e9e9" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }]
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8fad15" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }]
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }]
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }]
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#70b5ff" }]
  }
];

// Estilos melhorados para o tema claro com melhor nitidez
const lightMapStyles = [
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c0d6eb" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#c3e6a3" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f5d480" }]
  }
];

const Map = (props) => {
  const mapRef = useRef();
  const { points, darkMode } = props;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(5);
  const [center, setCenter] = useState({
    lat: -14.4284,
    lng: -55.2733
  });
  
  const [mapStyles, setMapStyles] = useState(darkMode ? darkMapStyles : lightMapStyles);

  // Atualiza os estilos do mapa quando o tema muda
  useEffect(() => {
    setMapStyles(darkMode ? darkMapStyles : lightMapStyles);
  }, [darkMode]);

  const handleCloseInfoWindown = () => {
    setSelectedUnit(null);
    setCenter({
      lat: -14.4284,
      lng: -55.2733
    });
    setZoom(5);
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 30, maxZoom: 15 }
  });

  const [selectedUnit, setSelectedUnit] = useState(null);

  const mapOptions = (isDark) => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControlOptions: {
      style: 2, // DROPDOWN_MENU
      position: 3 // RIGHT_TOP
    }
  });

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={{
          lat: -14.4284,
          lng: -55.2733
        }}
        center={center}
        zoom={zoom}
        defaultZoom={4}
        options={mapOptions(darkMode)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ]);
        }}
      >
        {clusters.map(marker => {
          const [longitude, latitude] = marker.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount
          } = marker.properties;

          if (isCluster) {
            return (
              <Marker
                key={`cluster-${marker.id}`}
                lat={latitude}
                lng={longitude}
              >
                <div
                  className="cluster map-cluster"
                  style={{
                    width: `${15 + (pointCount / points.length) * 30}px`,
                    height: `${15 + (pointCount / points.length) * 30}px`,
                    zIndex: 9999
                  }}
                  onClick={() => {
                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(marker.id),
                      20
                    );
                    mapRef.current.setZoom(expansionZoom);
                    mapRef.current.panTo({ lat: latitude, lng: longitude });
                  }}
                >
                  {pointCount}
                </div>
              </Marker>
            )
          }

          return (
            <Marker
              key={marker.properties._id || `marker-${latitude}-${longitude}`}
              lat={latitude}
              lng={longitude}
            >
              <div className="marker-container map-marker-container" style={{ position: 'relative' }}>
                <span className="marker-label" style={{
                  backgroundColor: darkMode ? 'rgba(40, 40, 40, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                  color: darkMode ? '#ffffff' : '#212529',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
                  border: darkMode ? '1px solid #555' : '1px solid #ddd'
                }}>
                  {marker?.properties?.name || 'Unidade'}
                </span>
                <img
                  alt="marker"
                  src="/images/ic_silo.png"
                  className="marker"
                  style={{ 
                    filter: darkMode ? 'brightness(1.2) contrast(1.1)' : 'none',
                    width: '28px',
                    height: '28px'
                  }}
                  onClick={() => {
                    setSelectedUnit(marker.properties.data);
                    setCenter({
                      lat: latitude,
                      lng: longitude
                    });
                  }}
                />
              </div>
            </Marker>
          )
        })}

        {selectedUnit &&
          <InfoWindow
            close={handleCloseInfoWindown}
            data={selectedUnit}
            lat={selectedUnit.lat}
            lng={selectedUnit.lng}
            setCenter={setCenter}
            darkMode={darkMode}
          />
        }
      </GoogleMapReact>
    </div>
  )
}

export default Map;
